<template>
   <RequestLoader :isLoading="isLoading" data-cy="profile-address-block" class="form-min-heights">
      <TextBox
         id="line1"
         v-model.trim="userInput.line_1"
         class="mb-6"
         :label="$t('user-data.address.line_1')"
         :hasError="showBlockErrors && $v.line_1.$error"
         :errorHelper="line1HelperData"
         @change="processInputs"
      />
      <TextBox
         id="line2"
         v-model.trim="userInput.line_2"
         class="mb-6"
         :label="$t('user-data.address.line_2')"
         :showIsOptional="true"
         :hasError="showBlockErrors && $v.line_2.$error"
         :errorHelper="line2HelperData"
         @change="processInputs"
      />
      <div class="grid gap-4 lg:grid-cols-2">
         <TextBox
            id="city"
            v-model.trim="userInput.city"
            class="mb-6"
            :label="$t('user-data.address.city')"
            :hasError="showBlockErrors && $v.city.$error"
            :errorHelper="cityHelperData"
            @change="processInputs"
         />
         <TextBox
            id="region"
            v-model.trim="userInput.region"
            class="mb-6"
            :label="$t('user-data.address.region')"
            :hasError="showBlockErrors && $v.region.$error"
            :errorHelper="regionHelperData"
            @change="processInputs"
         />
      </div>
      <div class="grid gap-4 lg:grid-cols-2">
         <TextBox
            id="postCode"
            v-model.trim="userInput.postalCode"
            class="mb-6"
            :label="$t('user-data.address.postcode')"
            :hasError="showBlockErrors && $v.postalCode.$error"
            :errorHelper="postCodeHelperData"
            @change="processInputs"
         />
         <SimpleSelect
            :selectedOption="userInput.country"
            :label="$t('user-data.address.country')"
            :options="countryOptions"
            :errorHelper="countryHelperData"
            :hasError="showBlockErrors && $v.country.$error"
            :disabled="!!props.block.data.settings.lockToCountry"
            @option-selected="updateCountry"
         />
      </div>
   </RequestLoader>
</template>

<script setup lang="ts">
import CountriesApi, { type Country } from "@/data/api-layer/countriesApi";
import { required, maxLength, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";
import type { UserAddress } from "@/data/api-layer/usersApi";

export interface ProfileAddressBlock extends FlowselBlock {
   data: {
      settings: {
         lockToCountry: string;
      };
   };
}
interface Props {
   block: ProfileAddressBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const emit = defineEmits(["update-valid", "update-answer"]);
const { t: $t } = useI18n();
const userStore = useUserStore();

const props = defineProps<Props>();
const isLoading = ref(true);
const countryList = ref<Country[]>([]);
const userInput = ref<UserAddress>({
   line_1: "",
   line_2: "",
   city: "",
   region: "",
   postalCode: "",
   country: "",
});

const rules = {
   line_1: {
      ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   line_2: {
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   city: {
      ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   region: {
      ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(250)),
      $autoDirty: true,
   },
   postalCode: {
      ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
      maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(10)),
      $autoDirty: true,
   },
   country: {
      ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
      $autoDirty: true,
   },
};
const $v: any = useVuelidate(rules, userInput);

// computed
//-----------------------------------------------
const countryOptions = computed(() => {
   // re-map our country codes to an array suitable for SimpleSelect component
   return countryList.value.map(({ alpha_2, name }) => ({ label: name, value: alpha_2 }));
});

// Textbox helper data
const line1HelperData = computed(() => {
   return getTextboxHelperData("line_1");
});
const line2HelperData = computed(() => {
   return getTextboxHelperData("line_2");
});
const cityHelperData = computed(() => {
   return getTextboxHelperData("city");
});
const regionHelperData = computed(() => {
   return getTextboxHelperData("region");
});
const postCodeHelperData = computed(() => {
   return getTextboxHelperData("postalCode");
});
const countryHelperData = computed(() => {
   return getTextboxHelperData("country");
});

// methods
//----------------------------------------------
/*
   Profile Block
   If the UserBlockAnswer answer is NULL, show loader (no inputs) and try to fetch any user profile data (e.g. another flow answer) to populate fields
   If the block answer is NOT NULL, use this to try to populate fields
*/

onMounted(async () => {
   try {
      await fetchCountryList();

      // Check userBlockAnswer for a previous response before attempting to use user data to pre-populate
      if (props.userBlockAnswer) {
         initialiseComponent(props.userBlockAnswer.data?.address);
      } else {
         await fetchUserData();
      }
   } catch (error) {
      console.error("Failed to fetch mount data for profile-address block: ", error);
   } finally {
      isLoading.value = false;
   }
});

/**
 * Fetch the country list for the country dropdown
 */
async function fetchCountryList() {
   try {
      const countryResp = await CountriesApi.getCountries();
      countryList.value = countryResp;
   } catch (e) {
      throw e; // Re-throw the error to let `onMounted` handle it
   }
}

// update country select choice
function updateCountry(country) {
   userInput.value.country = country;
   processInputs();
}

/**
 * Try and fetch user data that we might be able to use to pre-populate fields with.
 */
async function fetchUserData() {
   try {
      await userStore.setUser();
      initialiseComponent(userStore.user?.address);
   } catch (e) {
      throw e; // Re-throw the error to let `onMounted` handle it
   }
}

/**
 * update the inputs with any retrieved profile data
 * @param {object} userData
 */
function initialiseComponent(userAddress: UserAddress | null) {
   if (userAddress) {
      userInput.value = userAddress;
   } else {
      console.info("No address available for user");
   }

   // if the settings have locked the country we set the data to that
   if (props.block.data.settings.lockToCountry) {
      userInput.value.country = props.block.data.settings.lockToCountry;
   }
   processInputs(); // call on initialisation as the fields maybe have been pre-populated and now valid
}

/*
 * validates inputs before either emitting answer
 * or emitting an error
 */
function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid - this may be because the q is not required or because each field is filled as it should be
      const isFilledOut =
         userInput.value.line_1 !== "" &&
         userInput.value.city !== "" &&
         userInput.value.region !== "" &&
         userInput.value.postalCode &&
         userInput.value.country !== "";
      // Either submit null (if not required & not all necessary fields filled) or a formatted answer
      const answerToSubmit = isFilledOut
         ? {
              address: {
                 line_1: userInput.value.line_1,
                 line_2: userInput.value.line_2,
                 city: userInput.value.city,
                 region: userInput.value.region,
                 postal_code: userInput.value.postalCode,
                 country: userInput.value.country,
              },
           }
         : null;

      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

function getTextboxHelperData(vuelidate_input) {
   if ($v.value[vuelidate_input].$errors.length === 0) return null;
   return {
      text: $v.value[vuelidate_input].$errors[0].$message,
      code: $v.value[vuelidate_input].$errors[0].$validator,
   };
}
</script>

<style scoped>
/* It's worth setting these min heights because they make the appearance of this block after loading data much smoother */
.form-min-heights {
   min-height: 28rem;
}

@media (max-width: 47.9375rem) {
   .form-min-heights {
      min-height: 42.5rem;
   }
}
</style>
